import React, { useState, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link, useLocation } from 'react-router-dom';
import { Tag, Typography, Button, Divider, theme, ConfigProvider, Popover } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { ESensorMode } from '@/types';
import {
  normalizeModel,
  HEATIC2_PLUS_NORMALIZED,
  getDataTypesFromFlags,
  pluralize,
  computeHealthCounts,
  buildSensorBadgeConfigs,
  VERTICAL_DIVIDER_STYLE,
  SENSOR_TAG_STYLE,
  type DataTypeKey,
} from '../shared/sensorUtils';
import { useGetSensors3pGateways3p } from '@/components/studio/ScopingEditor/useGetSensors3pGateways3p';
import WarningIcon from '@/components/Icons/Warning';
import DoorIcon from '@/components/Icons/Door';
import CrosshairIcon from '@/components/Icons/Crosshair';
import { DATA_TYPE_TOOLTIPS } from '../shared/dataTypeTooltips';
import ExpandedCapabilities from '../shared/ExpandedCapabilities';
import { SENSOR_BADGE_ICONS, ICON_STYLE } from '../shared/sensorBadgeIcons';
import { useLinkButtonTheme } from '../shared/useLinkButtonTheme';

// Self-contained query: fetches room sensors + health + PIR config.
const ROOM_SETUP_QUERY = gql`
  query RoomSetupData($roomId: String!) {
    rooms(ids: [$roomId]) {
      data {
        id: room_id
        name
        pirZeroEnable: pir_zero_enable
        capacity {
          max
        }
        sensors {
          id: sensor_id
          mode
          model
          isEntrance: is_entrance
          isOnline: is_online
          isSleepTime: is_sleep_time
          connectionHealth: connection_health {
            status
          }
          config {
            use_pir
          }
        }
      }
    }
  }
`;

interface RoomSetupCardProps {
  roomId: string;
  spaceId: string;
}

const RoomSetupCard: React.FC<RoomSetupCardProps> = ({ roomId, spaceId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { search } = useLocation();
  const { token } = theme.useToken();
  const linkButtonTheme = useLinkButtonTheme();

  const { data, error } = useQuery(ROOM_SETUP_QUERY, {
    variables: { roomId },
    skip: !roomId,
  });

  const { sensors3p } = useGetSensors3pGateways3p({ spaceId });

  const roomData = data?.rooms?.data?.[0];
  const roomCapacity: number = roomData?.capacity?.max ?? 0;
  const sensors = useMemo(
    (): Array<{
      id: string;
      mode: string;
      model?: string;
      isEntrance: boolean;
      isOnline: boolean;
      isSleepTime: boolean;
      connectionHealth: { status: string };
      config?: { use_pir: boolean } | null;
    }> => roomData?.sensors ?? [],
    [roomData],
  );

  const trafficSensors = useMemo(
    () => sensors.filter((s) => s.mode === ESensorMode.TRAFFIC),
    [sensors],
  );
  const presenceSensors = useMemo(
    () => sensors.filter((s) => s.mode === ESensorMode.PRESENCE),
    [sensors],
  );

  // 3p sensors filtered to this room
  const motionSensors = useMemo(
    () => sensors3p.filter((s) => s.device_type === 'motion_sensor' && s.room_id === roomId),
    [sensors3p, roomId],
  );
  const deskSensors = useMemo(
    () => sensors3p.filter((s) => s.device_type === 'desk_sensor' && s.room_id === roomId),
    [sensors3p, roomId],
  );

  const hasTraffic = trafficSensors.length > 0;
  const hasPresence = presenceSensors.length > 0;
  const hasMotion = motionSensors.length > 0;
  const hasDesk = deskSensors.length > 0;
  const hasEntrance = sensors.some((s) => s.isEntrance);

  // PIR Zeroing: requires room-level pir_zero_enable=true + Traffic sensor +
  // Heatic 2+ Presence sensor with use_pir=true on the same sensor.
  const hasPir = useMemo(() => {
    if (!roomData?.pirZeroEnable) return false;
    const hasTrafficSensor = trafficSensors.length > 0;
    const hasHeatic2PlusWithPir = presenceSensors.some(
      (s) => normalizeModel(s.model ?? '') === HEATIC2_PLUS_NORMALIZED && s.config?.use_pir,
    );
    return hasTrafficSensor && hasHeatic2PlusWithPir;
  }, [roomData?.pirZeroEnable, trafficSensors, presenceSensors]);

  const roomDataTypes = useMemo(
    () => getDataTypesFromFlags({ hasPir, hasTraffic, hasPresence, hasMotion, hasDesk }),
    [hasPir, hasTraffic, hasPresence, hasMotion, hasDesk],
  );

  // Sensor Type Tags — the actual sensor types installed in the room with counts and icons
  const sensorTypeTags = useMemo(
    () =>
      buildSensorBadgeConfigs({
        traffic: trafficSensors.length,
        presence: presenceSensors.length,
        motion: motionSensors.length,
        desk: deskSensors.length,
      }),
    [trafficSensors.length, presenceSensors.length, motionSensors.length, deskSensors.length],
  );

  const { offlineCount, warningCount } = useMemo(() => computeHealthCounts(sensors), [sensors]);

  const issueCount = offlineCount + warningCount;

  // Data capabilities: what data the room can produce
  const dataCapabilities = useMemo(() => {
    const items: { title: string; description: string }[] = [];
    if (hasTraffic) {
      items.push({
        title: 'Entry/Exit Traffic Counts',
        description: `${pluralize(trafficSensors.length, 'traffic sensor')} deployed`,
      });
    }
    if (hasPresence) {
      items.push({
        title: 'Occupancy Estimates',
        description: `${pluralize(presenceSensors.length, 'presence sensor')} deployed`,
      });
    }
    if (deskSensors.length > 0) {
      items.push({
        title: 'Desk Occupancy',
        description: `${pluralize(deskSensors.length, 'desk sensor')} deployed`,
      });
    }
    return items;
  }, [hasTraffic, hasPresence, trafficSensors.length, presenceSensors.length, deskSensors.length]);

  // Features: higher-level processing on top of data capabilities
  const enabledFeatures = useMemo(() => {
    const items: { title: string; description: string; icon: React.ReactNode }[] = [];
    if (hasPir) {
      items.push({
        title: 'PIR Zeroing',
        description: 'Auto-calibration enabled',
        icon: <CrosshairIcon style={ICON_STYLE} />,
      });
    }
    if (hasEntrance) {
      items.push({
        title: 'Main Entrance Monitoring',
        description: 'Room-level IN/OUT traffic tracking enabled',
        icon: <DoorIcon style={ICON_STYLE} />,
      });
    }
    return items;
  }, [hasPir, hasEntrance]);

  // Don't render anything until data is loaded (but still show error if query failed)
  if (!roomData && !error) return null;

  return (
    <div
      style={{
        paddingLeft: token.paddingLG,
        paddingRight: token.paddingLG,
        paddingBottom: token.paddingXS,
        backgroundColor: token.colorBgContainer,
      }}
    >
      {/* Sensor badges row + offline badge + What's Enabled */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label="Toggle sensor details"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: token.marginMD,
          flexWrap: 'wrap',
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: token.marginXS, flexWrap: 'wrap' }}
        >
          {/* Data Type */}
          <Typography.Text type="secondary">Data Type:</Typography.Text>
          {roomDataTypes.length > 0 ? (
            roomDataTypes.map(({ label, color }) =>
              DATA_TYPE_TOOLTIPS[label as DataTypeKey] ? (
                <Popover
                  key={label}
                  content={DATA_TYPE_TOOLTIPS[label as DataTypeKey]}
                  overlayStyle={{ maxWidth: 300 }}
                >
                  <span>
                    <Tag color={color} bordered={false} style={{ margin: 0 }}>
                      {label}
                    </Tag>
                  </span>
                </Popover>
              ) : (
                <Tag key={label} color={color} bordered={false} style={{ margin: 0 }}>
                  {label}
                </Tag>
              ),
            )
          ) : (
            <Tag color="default" bordered={false} style={{ margin: 0 }}>
              No Data
            </Tag>
          )}
          {/* Sensor Installed — hidden when no sensors */}
          {sensorTypeTags.length > 0 && (
            <>
              <Divider type="vertical" style={VERTICAL_DIVIDER_STYLE} />
              <Typography.Text type="secondary">Installed:</Typography.Text>
              {sensorTypeTags.map((tag) => (
                <span
                  key={tag.label}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: token.marginXXS }}
                >
                  <Typography.Text>{tag.count} x</Typography.Text>
                  <Tag color={tag.color} bordered={false} style={SENSOR_TAG_STYLE}>
                    {SENSOR_BADGE_ICONS[tag.label]}
                    {tag.label}
                  </Tag>
                </span>
              ))}
            </>
          )}
          {/* Capacity */}
          {roomCapacity > 0 && (
            <>
              <Divider type="vertical" style={VERTICAL_DIVIDER_STYLE} />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.marginXXS }}>
                <Typography.Text type="secondary">Capacity:</Typography.Text>
                <Typography.Text>{roomCapacity}</Typography.Text>
              </span>
            </>
          )}

          {/* Divider between sensors/capacity and offline alert */}
          {issueCount > 0 && <Divider type="vertical" style={VERTICAL_DIVIDER_STYLE} />}
          {/* Offline badge */}
          {issueCount > 0 && (
            <Link
              to={`/devices${search}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: token.marginXXS,
                padding: `${token.paddingXXS}px ${token.paddingXS}px`,
                borderRadius: token.borderRadiusLG,
                fontSize: token.fontSizeSM,
                fontWeight: 500,
                backgroundColor: token.colorErrorBg,
                color: token.colorError,
                border: `1px solid ${token.colorErrorBorder}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <WarningIcon style={ICON_STYLE} />
              {offlineCount > 0 && `${pluralize(offlineCount, 'Sensor')} Offline`}
              {offlineCount > 0 && warningCount > 0 && ', '}
              {warningCount > 0 && `${warningCount} Warning`}
            </Link>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: token.marginMD }}>
          {/* What's Enabled toggle */}
          <ConfigProvider theme={linkButtonTheme}>
            <Button
              type="link"
              size="small"
              icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
              iconPosition="end"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              What&apos;s Enabled
            </Button>
          </ConfigProvider>
        </div>
      </div>

      {/* Expanded: enabled data capabilities + features */}
      {isExpanded && error && (
        <div style={{ marginTop: token.marginSM }}>
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            Unable to load sensor data.
          </Typography.Text>
        </div>
      )}
      {isExpanded && !error && (
        <ExpandedCapabilities
          dataCapabilities={dataCapabilities}
          enabledFeatures={enabledFeatures}
          emptyMessage="No data capabilities enabled for this room."
        />
      )}
    </div>
  );
};

export default RoomSetupCard;
